<template>
  <v-container fluid>
    <v-card>
      <v-card-title>Cierre de caja</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <v-select
              v-model="selectedMoneyId"
              :items="sessions"
              item-text="label"
              item-value="MONEY"
              label="Sesión de caja"
              outlined
              dense
              :loading="loadingSessions"
            >
              <template #selection="{ item }">
                <div class="d-flex align-center flex-wrap">
                  <span class="mr-2 font-weight-medium">{{ item.HOST }} - {{ item.HOSTSEQUENCE }}</span>
                  <v-chip x-small :color="item.DATEEND ? 'grey' : 'green'" dark>
                    {{ item.DATEEND ? 'Cerrada' : 'Abierta' }}
                  </v-chip>
                </div>
              </template>
              <template #item="{ item }">
                <div class="py-1 d-flex flex-column">
                  <div class="d-flex align-center">
                    <span class="mr-2 font-weight-medium">{{ item.HOST }} - {{ item.HOSTSEQUENCE }}</span>
                    <v-chip x-small :color="item.DATEEND ? 'grey' : 'green'" dark>
                      {{ item.DATEEND ? 'Cerrada' : 'Abierta' }}
                    </v-chip>
                  </div>
                  <div class="caption grey--text">
                    Inicio: {{ formatDate(item.DATESTART) }} | Final: {{ formatDate(item.DATEEND, 'Abierta') }}
                  </div>
                </div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-alert type="info" dense outlined class="mb-0">
          El cierre de caja debe ejecutarse desde el punto de venta iTPV. Esta pantalla solo muestra el resumen de sesiones cerradas o abiertas.
        </v-alert>

        <v-divider class="my-4"></v-divider>

        <div v-if="loadingSummary || loadingSessions">
          <v-row dense>
            <v-col cols="12" md="4" v-for="i in 3" :key="`sk-card-${i}`">
              <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" md="6" v-for="i in 2" :key="`sk-table-${i}`">
              <v-skeleton-loader type="table"></v-skeleton-loader>
            </v-col>
          </v-row>
        </div>

        <template v-else>
        <v-row dense class="mb-2" v-if="selectedSession">
          <v-col cols="12">
            <v-card outlined>
              <v-card-text class="py-3">
                <v-row dense>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text">Secuencia</div>
                    <div class="font-weight-medium">#{{ selectedSession.HOSTSEQUENCE }}</div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text">Estatus</div>
                    <v-chip x-small :color="selectedSession.DATEEND ? 'grey' : 'green'" dark>
                      {{ selectedSession.DATEEND ? 'Cerrada' : 'Abierta' }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text">Fecha inicio</div>
                    <div class="font-weight-medium">{{ formatDate(selectedSession.DATESTART) }}</div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="caption grey--text">Fecha final</div>
                    <div class="font-weight-medium">{{ formatDate(selectedSession.DATEEND, 'Abierta') }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text>
                <div class="caption grey--text">Pagos registrados</div>
                <div class="text-h6">{{ summary.paymentTotals.countPayments }}</div>
                <div class="caption mt-2 grey--text">Total pagos</div>
                <div class="text-subtitle-1">${{ formatMoney(summary.paymentTotals.totalPayments) }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text>
                <div class="caption grey--text">Tickets</div>
                <div class="text-h6">{{ summary.ticketTotals.countTickets }}</div>
                <div class="caption mt-2 grey--text">Venta bruta</div>
                <div class="text-subtitle-1">${{ formatMoney(summary.ticketTotals.grossTotal) }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text>
                <div class="caption grey--text">Impuestos totales</div>
                <div class="text-h6">${{ formatMoney(summary.taxTotals.totalTaxes) }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row dense class="mt-2">
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Pagos por método</v-card-title>
              <v-data-table
                :headers="paymentHeaders"
                :items="summary.paymentsByMethod"
                dense
                disable-pagination
                hide-default-footer
                no-data-text="Sin información"
              >
                <template #[`item.payment`]="{ item }">
                  {{ translatePaymentType(item.payment) }}
                </template>
                <template #[`item.total`]="{ item }">
                  ${{ formatMoney(item.total) }}
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Impuestos por categoría</v-card-title>
              <v-data-table
                :headers="taxHeaders"
                :items="summary.taxesByCategory"
                dense
                disable-pagination
                hide-default-footer
                no-data-text="Sin información"
              >
                <template #[`item.amount`]="{ item }">
                  ${{ formatMoney(item.amount) }}
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Movimientos de caja</v-card-title>
              <v-data-table
                :headers="movementHeaders"
                :items="summary.cashMovements"
                dense
                disable-pagination
                hide-default-footer
                no-data-text="Sin movimientos"
              >
                <template #[`item.dateNew`]="{ item }">
                  {{ formatDate(item.dateNew) }}
                </template>
                <template #[`item.payment`]="{ item }">
                  {{ translatePaymentType(item.payment) }}
                </template>
                <template #[`item.total`]="{ item }">
                  ${{ formatMoney(item.total) }}
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import * as cashClosureService from '@/services/cashClosureService';
import { formatDateTimeByTimezone } from '@/utils/dateTime';

export default {
  name: 'CashClosureView',
  data() {
    return {
      timezoneVersion: 0,
      sessions: [],
      selectedMoneyId: null,
      loadingSessions: false,
      loadingSummary: false,
      summary: {
        sessionInfo: null,
        paymentTotals: { countPayments: 0, totalPayments: 0 },
        ticketTotals: { countTickets: 0, grossTotal: 0 },
        taxTotals: { totalTaxes: 0 },
        paymentsByMethod: [],
        taxesByCategory: [],
        cashMovements: [],
      },
      paymentHeaders: [
        { text: 'Método', value: 'payment', sortable: false },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
      ],
      taxHeaders: [
        { text: 'Categoría', value: 'name', sortable: false },
        { text: 'Monto', value: 'amount', sortable: false, align: 'end' },
      ],
      movementHeaders: [
        { text: 'Fecha', value: 'dateNew', sortable: false },
        { text: 'Tipo', value: 'payment', sortable: false },
        { text: 'Descripción', value: 'description', sortable: false },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    selectedSession() {
      return this.sessions.find(session => session.MONEY === this.selectedMoneyId) || null;
    },
  },
  watch: {
    selectedMoneyId(newVal) {
      if (newVal) {
        this.loadSummary();
      } else {
        this.resetSummary();
      }
    },
  },
  async mounted() {
    window.addEventListener('app-timezone-changed', this.handleTimezoneChanged);
    await this.loadSessions();
  },
  beforeDestroy() {
    window.removeEventListener('app-timezone-changed', this.handleTimezoneChanged);
  },
  methods: {
    handleTimezoneChanged() {
      this.timezoneVersion += 1;
      this.$forceUpdate();
    },
    resetSummary() {
      this.summary = {
        sessionInfo: null,
        paymentTotals: { countPayments: 0, totalPayments: 0 },
        ticketTotals: { countTickets: 0, grossTotal: 0 },
        taxTotals: { totalTaxes: 0 },
        paymentsByMethod: [],
        taxesByCategory: [],
        cashMovements: [],
      };
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDate(value, fallback = 'N/A') {
      return formatDateTimeByTimezone(value, fallback);
    },
    translatePaymentType(value) {
      const key = String(value || '').toLowerCase();
      const map = {
        cash: 'Efectivo',
        magcard: 'Tarjeta',
        card: 'Tarjeta',
        free: 'Transferencia',
        creditcard: 'Tarjeta de crédito',
        debitcard: 'Tarjeta de débito',
        cheque: 'Tarjeta',
        check: 'Tarjeta',
        debt: 'Crédito',
        transfer: 'Transferencia',
        voucher: 'Vale',
        points: 'Puntos',
        cashrefund: 'Devolución en efectivo',
        cashout: 'Salida de dinero',
        cashin: 'Entrada de dinero',
      };
      return map[key] || value || '-';
    },
    sessionLabel(session) {
      const start = this.formatDate(session.DATESTART);
      const end = this.formatDate(session.DATEEND, 'Abierta');
      return `${start} - ${end}`;
    },
    async loadSessions() {
      this.loadingSessions = true;
      try {
        const rows = await cashClosureService.fetchCashSessions();
        this.sessions = rows.map(row => ({
          ...row,
          label: this.sessionLabel(row),
        }));
        if (this.sessions.length > 0) {
          this.selectedMoneyId = this.sessions[0].MONEY;
        }
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar sesiones de caja.', type: 'error' });
      } finally {
        this.loadingSessions = false;
      }
    },
    async loadSummary() {
      if (!this.selectedMoneyId) {
        this.$emit('show-alert', { message: 'Selecciona una sesión de caja.', type: 'warning' });
        return;
      }
      this.loadingSummary = true;
      try {
        this.summary = await cashClosureService.fetchCashSummary(this.selectedMoneyId);
      } catch (error) {
        this.$emit('show-alert', {
          message: `Error al consultar cierre: ${error.response?.data?.mensaje || error.message}`,
          type: 'error',
        });
      } finally {
        this.loadingSummary = false;
      }
    },
  },
};
</script>
